import * as React from "react";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ClientDetails } from "../pages/ClientDetails.tsx";
import { SpendDetails } from "../pages/SpendDetails.tsx";
import { SupplyChainCosts } from "../pages/SupplyChainCosts.tsx";
import { InvestmentSummary } from "../pages/InvestmentSummary.tsx";
import { ROICalculations } from "../pages/ROICalculations.tsx";
import { SameDaySetup } from "../pages/SameDaySetup.tsx";
import { SameDaySpendDetails } from "../pages/SameDaySpendDetails.tsx";
import { SameDayInvestment } from "../pages/SameDayInvestment.tsx";
import { SameDayROICalculations } from "../pages/SameDayROICalculations.tsx";
import { setClient, setSameDayMode } from "../Store/roiStore.ts";
import { hasPar360Solution } from "../constants.ts";
import { CUSTOMER_SITUATION, resolveCustomerSituation } from "../utils/customerSituation.ts";

export type WizardStepKey =
    | "client"
    | "samedaySetup"
    | "spendDetails"
    | "samedaySpend"
    | "currentCosts"
    | "investment"
    | "samedayInvestment"
    | "roi"
    | "samedayRoi";

export type WizardStep = {
    key: WizardStepKey;
    name: string;
    component: React.ReactElement;
};

const SAMEDAY_SOLUTION = "PAR SameDay";

export const useWizardSteps = (): WizardStep[] => {
    const dispatch = useDispatch();
    const currentSolutions: string[] = useSelector(
        (state: any) => state.inputFields.client?.currentParSolutions ?? []
    );
    const isSameDayOnly: boolean = useSelector(
        (state: any) => !!state.inputFields.sameDay?.isSameDayOnly
    );
    const client = useSelector((state: any) => state.inputFields.client);
    const situation = resolveCustomerSituation(client);

    // PAR SameDay-only mode is only meaningful when PAR SameDay is the SOLE
    // solution under analysis. The Setup step is shown as a confirmation in
    // that case; once another PAR service is added the analysis becomes a
    // multi-service ROI and the standard / combined flow takes over.
    const sameDayIsSoleSolution =
        currentSolutions.length === 1 &&
        currentSolutions[0] === SAMEDAY_SOLUTION;

    // Auto-clear a stranded SameDay-only flag when the wizard is no longer in
    // the sole-solution scenario (e.g. a rep ticked SameDay-only, then went
    // back to Client Details and added another PAR service). Without this the
    // SameDay-only flag would silently keep driving downstream pages even
    // though the Setup step that controls it has been hidden.
    useEffect(() => {
        if (!sameDayIsSoleSolution && isSameDayOnly) {
            dispatch(setSameDayMode({ isSameDayOnly: false }));
        }
    }, [sameDayIsSoleSolution, isSameDayOnly, dispatch]);

    // Incremental PAR 360 add-on only applies when PAR 360 Plus or Premium is
    // in scope. Clear a stranded flag if the rep deselects both.
    useEffect(() => {
        if (situation === CUSTOMER_SITUATION.PAR360_ADDON && !hasPar360Solution(currentSolutions)) {
            dispatch(setClient({ customerSituation: CUSTOMER_SITUATION.NEW, isPar360AddOn: false }));
        }
    }, [situation, currentSolutions, dispatch]);

    return useMemo(() => {
        const steps: WizardStep[] = [
            { key: "client", name: "Client Details", component: <ClientDetails /> },
        ];

        if (sameDayIsSoleSolution) {
            steps.push({
                key: "samedaySetup",
                name: "PAR SameDay Setup",
                component: <SameDaySetup />,
            });
        }

        const inSameDayOnlyFlow = sameDayIsSoleSolution && isSameDayOnly;

        if (inSameDayOnlyFlow) {
            steps.push({
                key: "samedaySpend",
                name: "PAR SameDay Spend Details",
                component: <SameDaySpendDetails />,
            });
            steps.push({
                key: "samedayInvestment",
                name: "PAR Excellence Investment",
                component: <SameDayInvestment />,
            });
            steps.push({
                key: "samedayRoi",
                name: "ROI Calculations",
                component: <SameDayROICalculations />,
            });
        } else {
            steps.push({
                key: "spendDetails",
                name: "Spend Details by Facility",
                component: <SpendDetails />,
            });
            steps.push({
                key: "currentCosts",
                name: "Client's Current & Future Costs",
                component: <SupplyChainCosts />,
            });
            steps.push({
                key: "investment",
                name: "PAR Excellence Investment",
                component: <InvestmentSummary />,
            });
            steps.push({
                key: "roi",
                name: "ROI Calculations",
                component: <ROICalculations />,
            });
        }

        return steps;
    }, [sameDayIsSoleSolution, isSameDayOnly]);
};
